import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, query, orderBy, getDocs } from "firebase/firestore";

const S = {
  input:{width:"100%",padding:"6px 10px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",background:"#fff",boxSizing:"border-box"},
  btn:{fontSize:11,padding:"3px 9px",borderRadius:6,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer"},
};

function formatTime(ts){
  if(!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"});
}

function highlightText(text, query){
  if(!query||!text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if(idx===-1) return text;
  return (
    <>
      {text.slice(0,idx)}
      <mark style={{background:"#FFF3CD",padding:"0 1px",borderRadius:2}}>{text.slice(idx,idx+query.length)}</mark>
      {text.slice(idx+query.length)}
    </>
  );
}

// Search within a single thread/channel
export function ThreadSearch({ messages, onJumpTo, searchRef }){
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [current, setCurrent] = useState(0);
  const inputRef = useRef();

  // Ctrl+F handler
  useEffect(()=>{
    const handler = (e)=>{
      if((e.ctrlKey||e.metaKey) && e.key==="f"){
        e.preventDefault();
        setOpen(true);
        setTimeout(()=>inputRef.current?.focus(),50);
      }
      if(e.key==="Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return ()=>document.removeEventListener("keydown", handler);
  },[]);

  // Expose open function via ref
  useEffect(()=>{
    if(searchRef) searchRef.current = { open: ()=>{ setOpen(true); setTimeout(()=>inputRef.current?.focus(),50); } };
  },[searchRef]);

  useEffect(()=>{
    if(!q.trim()){ setResults([]); return; }
    const matched = messages.filter(m=>
      (m.text||"").toLowerCase().includes(q.toLowerCase()) ||
      (m.attachment||"").toLowerCase().includes(q.toLowerCase())
    );
    setResults(matched);
    setCurrent(0);
  },[q, messages]);

  const jump = (idx) => {
    const msg = results[idx];
    if(msg && onJumpTo) onJumpTo(msg.firestoreId);
  };

  const next = () => { const n=(current+1)%results.length; setCurrent(n); jump(n); };
  const prev = () => { const n=(current-1+results.length)%results.length; setCurrent(n); jump(n); };

  useEffect(()=>{ if(results.length>0) jump(0); },[results]);

  if(!open) return (
    <button onClick={()=>{ setOpen(true); setTimeout(()=>inputRef.current?.focus(),50); }}
      title="Search messages (Ctrl+F)"
      style={{...S.btn,padding:"4px 8px",fontSize:13}}>🔍</button>
  );

  return (
    <div style={{display:"flex",alignItems:"center",gap:6,background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:8,padding:"4px 8px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
      <span style={{fontSize:13,color:"#aaa"}}>🔍</span>
      <input ref={inputRef} style={{...S.input,width:180,border:"none",padding:"2px 4px",outline:"none"}}
        value={q} onChange={e=>setQ(e.target.value)}
        placeholder="Search in conversation..."
        onKeyDown={e=>{ if(e.key==="Enter") next(); if(e.key==="Escape"){ setOpen(false); setQ(""); }}}
      />
      {q&&<span style={{fontSize:11,color:"#888",whiteSpace:"nowrap"}}>
        {results.length>0?`${current+1}/${results.length}`:"No results"}
      </span>}
      {results.length>1&&<>
        <button style={S.btn} onClick={prev}>↑</button>
        <button style={S.btn} onClick={next}>↓</button>
      </>}
      <button style={S.btn} onClick={()=>{ setOpen(false); setQ(""); }}>✕</button>
    </div>
  );
}

// Global search across ALL message threads (admin only)
export function GlobalMessageSearch({ isAdmin, currentUserName }){
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef();

  const searchAll = async () => {
    if(!q.trim()) return;
    setSearching(true);
    const found = [];

    try {
      // Search Messages (client threads)
      const threadsSnap = await getDocs(collection(db,"messages"));
      for(const threadDoc of threadsSnap.docs){
        const threadData = threadDoc.data();
        const msgsSnap = await getDocs(
          query(collection(db,"messages",threadDoc.id,"thread"), orderBy("createdAt","asc"))
        );
        msgsSnap.docs.forEach(m=>{
          const msg = m.data();
          if((msg.text||"").toLowerCase().includes(q.toLowerCase())){
            found.push({
              id: m.id, threadId: threadDoc.id,
              source: "message",
              client: threadData.clientName||threadDoc.id,
              text: msg.text,
              sender: msg.senderName,
              date: msg.createdAt?.toDate?.() || new Date(),
            });
          }
        });
      }

      // Search Team Chat channels (admin only)
      if(isAdmin){
        const channels = ["general","bookkeeping","tax","tasks"];
        for(const ch of channels){
          try{
            const snap = await getDocs(query(collection(db,"teamChat_"+ch), orderBy("createdAt","asc")));
            snap.docs.forEach(m=>{
              const msg = m.data();
              if((msg.text||"").toLowerCase().includes(q.toLowerCase())){
                found.push({
                  id: m.id, threadId: ch,
                  source: "teamchat",
                  channel: ch,
                  text: msg.text,
                  sender: msg.senderName,
                  date: msg.createdAt?.toDate?.() || new Date(),
                });
              }
            });
          }catch(e){}
        }
      }

      found.sort((a,b)=>new Date(b.date)-new Date(a.date));
      setResults(found);
    } catch(e){ console.error(e); }
    setSearching(false);
  };

  if(!open) return (
    <button onClick={()=>{ setOpen(true); setTimeout(()=>inputRef.current?.focus(),50); }}
      title="Search across all messages"
      style={{fontSize:12,padding:"5px 10px",borderRadius:8,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
      🔍 Search all messages
    </button>
  );

  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"flex-start",justifyContent:"center",zIndex:2000,paddingTop:60}}>
      <div style={{background:"#fff",borderRadius:14,width:600,maxHeight:"75vh",display:"flex",flexDirection:"column",boxShadow:"0 8px 40px rgba(0,0,0,0.2)"}}>
        <div style={{padding:"14px 16px",borderBottom:"0.5px solid #e0dfd8",flexShrink:0}}>
          <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
            <span style={{fontSize:16}}>🔍</span>
            <input ref={inputRef} style={{...S.input,fontSize:13,flex:1}}
              value={q} onChange={e=>setQ(e.target.value)}
              placeholder="Search across all messages and team chat..."
              onKeyDown={e=>{ if(e.key==="Enter") searchAll(); if(e.key==="Escape"){ setOpen(false); setQ(""); setResults([]); }}}
              autoFocus
            />
            <button onClick={searchAll} disabled={searching||!q.trim()}
              style={{...S.btn,background:"#C0111F",color:"#fff",border:"none",padding:"5px 12px",fontSize:12,whiteSpace:"nowrap"}}>
              {searching?"Searching...":"Search"}
            </button>
            <button onClick={()=>{ setOpen(false); setQ(""); setResults([]); }} style={S.btn}>✕</button>
          </div>
          {results.length>0&&<div style={{fontSize:11,color:"#888"}}>{results.length} result{results.length!==1?"s":""} across all conversations</div>}
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"8px 0"}}>
          {results.length===0&&!searching&&q&&(
            <div style={{textAlign:"center",padding:"32px",color:"#aaa",fontSize:13}}>No results found for "{q}"</div>
          )}
          {results.map((r,i)=>(
            <div key={i} style={{padding:"10px 16px",borderBottom:"0.5px solid #f5f5f0",cursor:"default"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:10,padding:"1px 7px",borderRadius:10,
                    background:r.source==="teamchat"?"#E6F1FB":"#EAF3DE",
                    color:r.source==="teamchat"?"#185FA5":"#3B6D11",fontWeight:500}}>
                    {r.source==="teamchat"?`#${r.channel}`:`💬 ${r.client}`}
                  </span>
                  <span style={{fontSize:11,color:"#888"}}>{r.sender}</span>
                </div>
                <span style={{fontSize:10,color:"#aaa"}}>{r.date instanceof Date ? r.date.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"}) : ""}</span>
              </div>
              <div style={{fontSize:12,color:"#555",lineHeight:1.5}}>
                {highlightText(r.text, q)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
